import { msgs } from "../define";

/**
 * Translated text for every API error code the POS, payment and checkout screens can receive
 * ("apiError.<code>"). errorText() uses these instead of the server's English message.
 * test/errors.test.ts checks that every code thrown by the POS/payment API code has a key here.
 */
export const errors = msgs({
  en: {
    "apiError.already_decided": "This request was already decided.",
    "apiError.already_locked": "This day is already locked.",
    "apiError.already_reversed": "This redemption was already reversed.",
    "apiError.amount_invalid": "The amount must be greater than zero.",
    "apiError.amount_over_balance": "The amount is more than the balance due.",
    "apiError.amount_required": "Enter the amount.",
    "apiError.approval_invalid": "This approval is no longer valid. Ask the manager again.",
    "apiError.approval_required": "A manager needs to approve this.",
    "apiError.bad_manager_pin": "That PIN doesn't belong to a manager of this location.",
    "apiError.bad_origin": "The request was blocked for security reasons. Reload the page and try again.",
    "apiError.bad_request": "The request was not accepted. Check the details and try again.",
    "apiError.balance_due": "There is still an amount to pay. Take a payment instead.",
    "apiError.billing_read_only": "Your subscription is unpaid, so the salon is read-only. The owner can fix billing in Plan & billing.",
    "apiError.billing_suspended": "This salon is suspended because the subscription is unpaid. The owner can reactivate it.",
    "apiError.branch_required": "Select a branch first.",
    "apiError.credit_disabled": "Credit top-ups are turned off (Settings → Credit & rewards).",
    "apiError.credit_not_for_topup": "Credit can't pay for a credit top-up. Pay the top-up with cash or card.",
    "apiError.csrf": "Your session needs a refresh. Reload the page and try again.",
    "apiError.currency_mismatch": "The currency doesn't match this sale or cash drawer.",
    "apiError.customer_locked": "The customer can't be changed any more on this sale.",
    "apiError.customer_required": "Choose the customer first.",
    "apiError.email_required": "Enter the customer's e-mail address.",
    "apiError.empty_order": "Add an item first.",
    "apiError.feature_not_enabled": "This needs an add-on that isn't enabled for your salon.",
    "apiError.forbidden": "You don't have permission to do that.",
    "apiError.future_day": "A day can be locked once it has started.",
    "apiError.idempotency_conflict": "This payment was already started with different details. Start a new payment.",
    "apiError.inactive": "This item is no longer sold.",
    "apiError.insufficient_credit": "Not enough credit.",
    "apiError.internal": "Something went wrong on the server. Please try again.",
    "apiError.invalid_payment_transition": "This payment can't change to that status.",
    "apiError.invalid_state": "This can't be done in the current state. Refresh and try again.",
    "apiError.invalid_token": "Your session has expired. Please sign in again.",
    "apiError.kiosk_locked": "Locked — enter your PIN.",
    "apiError.line_no_options": "This line has no options.",
    "apiError.manual_payment_final": "Manually recorded payments can't change status.",
    "apiError.no_card_payment": "This sale wasn't paid online. Refund it with the method it was paid with.",
    "apiError.no_cash_session": "The business isn't open yet (cash drawer). Open business, then take cash.",
    "apiError.no_online_methods": "Your Stripe account has no payment method for this currency. Check the payment methods in Stripe.",
    "apiError.not_for_sale": "This is a salon-use product and isn't sold at the counter.",
    "apiError.not_found": "Not found — it may have been removed. Refresh and try again.",
    "apiError.not_paid": "This sale isn't paid yet.",
    "apiError.not_redeemable": "Only services or products can be paid with a package.",
    "apiError.not_refundable": "This can't be refunded under its terms.",
    "apiError.note_required": "Add a note explaining the difference before closing.",
    "apiError.nothing_due": "Nothing is left to pay on this sale.",
    "apiError.offline_payment": "You're offline. Payments are never queued — reconnect and try again.",
    "apiError.onboarding_required": "Finish setting up your salon first.",
    "apiError.order_closed": "This sale is closed and can't be changed. Use a refund instead.",
    "apiError.order_held": "This sale is on hold. Resume it first.",
    "apiError.order_locked": "A payment was already taken; items and prices are locked. Use a refund instead.",
    "apiError.package_branch": "This package can't be used or sold at this branch.",
    "apiError.package_closed": "This package is closed.",
    "apiError.package_exhausted": "No sessions or value left on this package.",
    "apiError.package_expired": "This package has expired.",
    "apiError.package_frozen": "This package is frozen. Unfreeze it first.",
    "apiError.package_insufficient": "Not enough value left on this package.",
    "apiError.package_line_no_options": "A package line has no options.",
    "apiError.package_not_eligible": "This item isn't covered by the package.",
    "apiError.package_other_customer": "This package belongs to another customer.",
    "apiError.package_over_line": "The package amount is more than the line amount.",
    "apiError.package_unavailable": "This package can no longer be used.",
    "apiError.payment_closed": "This payment is already finished.",
    "apiError.price_required": "Set the price of every item before charging.",
    "apiError.qty_invalid": "The quantity must be a non-zero whole number.",
    "apiError.rate_limited": "Too many attempts. Please wait a few minutes and try again.",
    "apiError.redeemed_no_discount": "This line is paid with a package session and can't also be discounted. Remove the package first.",
    "apiError.reference_required": "Enter the terminal approval code or bank reference.",
    "apiError.refund_amount_required": "Enter the amount to refund.",
    "apiError.refund_line_duplicate": "Each line can appear only once in a refund.",
    "apiError.refund_method_required": "Choose how the money is returned.",
    "apiError.refund_not_package_paid": "This line was not paid with a package.",
    "apiError.refund_over_line": "The refund is more than what is left on this line.",
    "apiError.refund_over_paid": "The refund is more than what was paid.",
    "apiError.refund_over_quote": "The refund is more than the pro-rata amount.",
    "apiError.restock_done": "Those units were already returned to stock.",
    "apiError.restock_invalid": "Only sold products can go back to stock (at most the quantity sold).",
    "apiError.self_approval": "A request can't be approved by the person who made it.",
    "apiError.service_unavailable": "This service isn't available at this branch.",
    "apiError.session_closed": "This business session (cash drawer) is already closed.",
    "apiError.session_open": "Business is already open at this branch (cash drawer open).",
    "apiError.split_duplicate": "The same person appears twice with the same role.",
    "apiError.split_refund": "The card refund is more than any single online payment. Refund the rest with another method.",
    "apiError.stripe_account_changed": "The salon's Stripe account has changed. Start a new payment or refund in the Stripe dashboard.",
    "apiError.stripe_disabled": "Online card payments aren't enabled. Take cash, card terminal or bank transfer instead.",
    "apiError.stripe_keys_disabled": "Pasting Stripe keys is disabled. Use Connect instead.",
    "apiError.stripe_not_available": "Online card payments aren't available yet.",
    "apiError.stripe_not_connected": "Connect the salon's Stripe account first (owner portal).",
    "apiError.stripe_payment_pending": "An online payment is still open for this sale. Wait for it or cancel it first.",
    "apiError.stripe_verification_pending": "Stripe is still verifying the account. Online payments start once Stripe enables charges.",
    "apiError.tendered_short": "Cash received must be at least the amount.",
    "apiError.topup_amount_required": "Enter the top-up amount.",
    "apiError.topup_below_min": "This is below the smallest top-up (Settings → Credit & rewards).",
    "apiError.topup_no_discount": "Credit top-ups can't be discounted. Use the bonus tiers in Settings → Credit & rewards.",
    "apiError.topup_single_amount": "A credit top-up has one amount: no discount, options or quantity.",
    "apiError.total_below_paid": "The total can't be lower than what was already paid.",
    "apiError.unauthorized": "Please sign in again.",
    "apiError.level_in_use": "This level is still in use (team members, price items or commission rules). Use “Merge into…” to move them, or change them first.",
    "apiError.role_in_use": "This role is still in use (commission rules or line-type defaults). Use “Merge into…” to move them, or change them first.",
    "apiError.unknown_level": "Unknown staff level. Levels are set in Team → Levels & roles.",
    "apiError.merge_same": "Choose a different entry to merge into.",
    "apiError.industry_required": "Choose your type of business first.",
    "apiError.unknown_modifier": "That price option isn't available for this service.",
    "apiError.unknown_role": "Unknown performance role. Roles are set in Team → Levels & roles.",
    "apiError.unknown_variant": "That option isn't available for this item.",
    "apiError.usage_service_only": "Usage can only be recorded for a service line.",
    "apiError.use_credit_refund": "Refund stored credit from the customer's Credit tab.",
    "apiError.use_package_refund": "Refund a package from the customer's Packages tab.",
    "apiError.payment_method_invalid": "That payment method can't be used here. Choose another one.",
    "apiError.refund_over_tip": "That's more than the tip left to refund.",
    "apiError.feature_unavailable": "This option isn't available for this shop.",
    "apiError.requested_needs_staff": "Choose the staff member the customer asked for.",
    "apiError.tips_off": "Tips are turned off for this shop.",
    "apiError.tip_custom_off": "Custom tip amounts are turned off. Choose one of the suggested tips.",
    "apiError.credit_not_for_tip": "Store credit can't pay the tip. Take the tip in cash or by card.",
    "error.status.400": "The request was not accepted. Check the details and try again.",
    "error.status.404": "Not found — it may have been removed. Refresh and try again.",
    "error.status.409": "This conflicts with a recent change. Refresh and try again.",
  },
  zh: {
    "apiError.already_decided": "此请求已被处理。",
    "apiError.already_locked": "这一天已经锁定。",
    "apiError.already_reversed": "这次核销已经撤销过了。",
    "apiError.amount_invalid": "金额必须大于零。",
    "apiError.amount_over_balance": "金额超过了应付余额。",
    "apiError.amount_required": "请输入金额。",
    "apiError.approval_invalid": "此审批已失效，请再次请经理审批。",
    "apiError.approval_required": "需要经理审批。",
    "apiError.bad_manager_pin": "该 PIN 不属于本店的经理。",
    "apiError.bad_origin": "出于安全原因，请求被拦截。请刷新页面后重试。",
    "apiError.bad_request": "请求未被接受，请检查内容后重试。",
    "apiError.balance_due": "还有未付金额，请先收款。",
    "apiError.billing_read_only": "订阅未付款，店铺目前为只读。店主可在“套餐与账单”中处理。",
    "apiError.billing_suspended": "订阅未付款，店铺已暂停。店主可以重新启用。",
    "apiError.branch_required": "请先选择门店。",
    "apiError.credit_disabled": "储值充值已关闭（设置 → 储值与奖励）。",
    "apiError.credit_not_for_topup": "储值不能用来支付储值充值，请用现金或刷卡支付。",
    "apiError.csrf": "会话需要刷新，请刷新页面后重试。",
    "apiError.currency_mismatch": "币种与此订单或钱箱不一致。",
    "apiError.customer_locked": "此订单已不能更换顾客。",
    "apiError.customer_required": "请先选择顾客。",
    "apiError.email_required": "请输入顾客的电子邮箱。",
    "apiError.empty_order": "请先添加项目。",
    "apiError.feature_not_enabled": "此功能需要开通相应的附加模块。",
    "apiError.forbidden": "您没有执行此操作的权限。",
    "apiError.future_day": "这一天开始后才能锁定。",
    "apiError.idempotency_conflict": "此笔付款已用不同的内容提交过，请重新开始一笔付款。",
    "apiError.inactive": "此项目已停售。",
    "apiError.insufficient_credit": "储值余额不足。",
    "apiError.internal": "服务器出错了，请重试。",
    "apiError.invalid_payment_transition": "此付款不能变更为该状态。",
    "apiError.invalid_state": "当前状态下无法执行此操作，请刷新后重试。",
    "apiError.invalid_token": "登录已过期，请重新登录。",
    "apiError.kiosk_locked": "已锁定——请输入您的 PIN。",
    "apiError.line_no_options": "此项目没有可选项。",
    "apiError.manual_payment_final": "手动记录的付款不能更改状态。",
    "apiError.no_card_payment": "此订单不是线上支付的，请用原支付方式退款。",
    "apiError.no_cash_session": "还未开始营业（钱箱未开班）。请先开始营业，再收现金。",
    "apiError.no_online_methods": "您的 Stripe 账户没有支持此币种的支付方式，请在 Stripe 中检查。",
    "apiError.not_for_sale": "这是店内耗材，不在前台销售。",
    "apiError.not_found": "未找到，可能已被删除。请刷新后重试。",
    "apiError.not_paid": "此订单尚未付款。",
    "apiError.not_redeemable": "只有服务或商品可以用套餐支付。",
    "apiError.not_refundable": "按条款此项不可退款。",
    "apiError.note_required": "关班前请填写差额说明。",
    "apiError.nothing_due": "此订单已无应付金额。",
    "apiError.offline_payment": "当前离线，付款不会排队，请联网后重试。",
    "apiError.onboarding_required": "请先完成店铺设置。",
    "apiError.order_closed": "此订单已关闭，不能修改，请改用退款。",
    "apiError.order_held": "此订单已挂单，请先恢复。",
    "apiError.order_locked": "已收过款，项目和价格已锁定，请改用退款。",
    "apiError.package_branch": "此套餐不能在本门店使用或销售。",
    "apiError.package_closed": "此套餐已关闭。",
    "apiError.package_exhausted": "此套餐已没有剩余次数或余额。",
    "apiError.package_expired": "此套餐已过期。",
    "apiError.package_frozen": "此套餐已冻结，请先解冻。",
    "apiError.package_insufficient": "此套餐余额不足。",
    "apiError.package_line_no_options": "套餐项目没有可选项。",
    "apiError.package_not_eligible": "此项目不在套餐范围内。",
    "apiError.package_other_customer": "此套餐属于其他顾客。",
    "apiError.package_over_line": "套餐抵扣金额超过了项目金额。",
    "apiError.package_unavailable": "此套餐已不能使用。",
    "apiError.payment_closed": "此笔付款已结束。",
    "apiError.price_required": "收款前请先为所有项目设置价格。",
    "apiError.qty_invalid": "数量必须是非零整数。",
    "apiError.rate_limited": "尝试次数过多，请稍等几分钟再试。",
    "apiError.redeemed_no_discount": "此项目已用套餐次数支付，不能再打折。请先移除套餐。",
    "apiError.reference_required": "请输入刷卡机授权码或银行转账参考号。",
    "apiError.refund_amount_required": "请输入退款金额。",
    "apiError.refund_line_duplicate": "每个项目在一次退款中只能出现一次。",
    "apiError.refund_method_required": "请选择退款方式。",
    "apiError.refund_not_package_paid": "此项目不是用套餐支付的。",
    "apiError.refund_over_line": "退款金额超过了此项目的剩余金额。",
    "apiError.refund_over_paid": "退款金额超过了已付金额。",
    "apiError.refund_over_quote": "退款金额超过了按比例计算的金额。",
    "apiError.restock_done": "这些数量已经退回库存了。",
    "apiError.restock_invalid": "只有售出的商品可以退回库存（不超过售出数量）。",
    "apiError.self_approval": "申请人不能审批自己的申请。",
    "apiError.service_unavailable": "此服务在本门店不可用。",
    "apiError.session_closed": "此营业班次（钱箱）已结束。",
    "apiError.session_open": "本门店已经开始营业（钱箱已开班）。",
    "apiError.split_duplicate": "同一人以相同角色出现了两次。",
    "apiError.split_refund": "刷卡退款超过了任何一笔线上付款，其余部分请用其他方式退款。",
    "apiError.stripe_account_changed": "店铺的 Stripe 账户已变更，请重新发起付款，或在 Stripe 后台退款。",
    "apiError.stripe_disabled": "线上刷卡未启用，请改收现金、刷卡机或银行转账。",
    "apiError.stripe_keys_disabled": "已禁用粘贴 Stripe 密钥，请改用 Connect。",
    "apiError.stripe_not_available": "线上刷卡暂不可用。",
    "apiError.stripe_not_connected": "请先连接店铺的 Stripe 账户（店主后台）。",
    "apiError.stripe_payment_pending": "此订单还有一笔进行中的线上付款，请等待或先取消。",
    "apiError.stripe_verification_pending": "Stripe 仍在审核账户，开通收款后即可线上支付。",
    "apiError.tendered_short": "实收现金不能少于金额。",
    "apiError.topup_amount_required": "请输入充值金额。",
    "apiError.topup_below_min": "低于最低充值金额（设置 → 储值与奖励）。",
    "apiError.topup_no_discount": "储值充值不能打折，请使用设置中的赠送档位。",
    "apiError.topup_single_amount": "储值充值只有一个金额：不能打折、选项或数量。",
    "apiError.total_below_paid": "订单总额不能低于已付金额。",
    "apiError.unauthorized": "请重新登录。",
    "apiError.level_in_use": "该级别仍在使用中（员工、加价项或提成规则）。请用“合并到…”转移，或先修改相关设置。",
    "apiError.role_in_use": "该角色仍在使用中（提成规则或默认用途）。请用“合并到…”转移，或先修改相关设置。",
    "apiError.unknown_level": "未知的员工级别。级别在 团队 → 级别与角色 中设置。",
    "apiError.merge_same": "请选择另一个项目进行合并。",
    "apiError.industry_required": "请先选择你的行业。",
    "apiError.unknown_modifier": "此服务没有该价格选项。",
    "apiError.unknown_role": "未知的业绩角色，请在 团队 → 级别与角色 中设置。",
    "apiError.unknown_variant": "此项目没有该选项。",
    "apiError.usage_service_only": "只有服务项目可以记录耗材用量。",
    "apiError.use_credit_refund": "储值请在顾客的“储值”页退款。",
    "apiError.use_package_refund": "套餐请在顾客的“套餐”页退款。",
    "apiError.payment_method_invalid": "此付款方式不可用，请选择其他方式。",
    "apiError.refund_over_tip": "超出可退还的小费金额。",
    "apiError.feature_unavailable": "本店无法使用此选项。",
    "apiError.requested_needs_staff": "请选择顾客点名的服务人员。",
    "apiError.tips_off": "本店已关闭小费。",
    "apiError.tip_custom_off": "已关闭自定义小费金额，请选择建议的小费。",
    "apiError.credit_not_for_tip": "储值不能支付小费，请用现金或刷卡收取小费。",
    "error.status.400": "请求未被接受，请检查内容后重试。",
    "error.status.404": "未找到，可能已被删除。请刷新后重试。",
    "error.status.409": "与最近的修改冲突，请刷新后重试。",
  },
  ms: {
    "apiError.already_decided": "Permintaan ini telah diputuskan.",
    "apiError.already_locked": "Hari ini telah dikunci.",
    "apiError.already_reversed": "Penebusan ini telah dibatalkan.",
    "apiError.amount_invalid": "Jumlah mesti lebih daripada sifar.",
    "apiError.amount_over_balance": "Jumlah melebihi baki yang perlu dibayar.",
    "apiError.amount_required": "Masukkan jumlah.",
    "apiError.approval_invalid": "Kelulusan ini tidak sah lagi. Minta pengurus sekali lagi.",
    "apiError.approval_required": "Pengurus perlu meluluskan ini.",
    "apiError.bad_manager_pin": "PIN itu bukan milik pengurus lokasi ini.",
    "apiError.bad_origin": "Permintaan disekat atas sebab keselamatan. Muat semula halaman dan cuba lagi.",
    "apiError.bad_request": "Permintaan tidak diterima. Semak butiran dan cuba lagi.",
    "apiError.balance_due": "Masih ada baki untuk dibayar. Terima bayaran dahulu.",
    "apiError.billing_read_only": "Langganan belum dibayar, jadi salun hanya boleh dibaca. Pemilik boleh membetulkannya dalam Pelan & bil.",
    "apiError.billing_suspended": "Salun ini digantung kerana langganan belum dibayar. Pemilik boleh mengaktifkannya semula.",
    "apiError.branch_required": "Pilih cawangan dahulu.",
    "apiError.credit_disabled": "Tambah nilai kredit dimatikan (Tetapan → Kredit & ganjaran).",
    "apiError.credit_not_for_topup": "Kredit tidak boleh membayar tambah nilai kredit. Bayar dengan tunai atau kad.",
    "apiError.csrf": "Sesi anda perlu dimuat semula. Muat semula halaman dan cuba lagi.",
    "apiError.currency_mismatch": "Mata wang tidak sepadan dengan jualan atau laci tunai ini.",
    "apiError.customer_locked": "Pelanggan tidak boleh ditukar lagi untuk jualan ini.",
    "apiError.customer_required": "Pilih pelanggan dahulu.",
    "apiError.email_required": "Masukkan alamat e-mel pelanggan.",
    "apiError.empty_order": "Tambah item dahulu.",
    "apiError.feature_not_enabled": "Ini memerlukan tambahan yang belum diaktifkan untuk salun anda.",
    "apiError.forbidden": "Anda tiada kebenaran untuk tindakan ini.",
    "apiError.future_day": "Hari boleh dikunci setelah ia bermula.",
    "apiError.idempotency_conflict": "Bayaran ini telah dimulakan dengan butiran berbeza. Mulakan bayaran baharu.",
    "apiError.inactive": "Item ini tidak dijual lagi.",
    "apiError.insufficient_credit": "Kredit tidak mencukupi.",
    "apiError.internal": "Berlaku ralat pada pelayan. Sila cuba lagi.",
    "apiError.invalid_payment_transition": "Bayaran ini tidak boleh ditukar kepada status itu.",
    "apiError.invalid_state": "Ini tidak boleh dilakukan dalam keadaan semasa. Muat semula dan cuba lagi.",
    "apiError.invalid_token": "Sesi anda telah tamat. Sila log masuk semula.",
    "apiError.kiosk_locked": "Dikunci — masukkan PIN anda.",
    "apiError.line_no_options": "Baris ini tiada pilihan.",
    "apiError.manual_payment_final": "Bayaran yang direkod secara manual tidak boleh menukar status.",
    "apiError.no_card_payment": "Jualan ini tidak dibayar dalam talian. Pulangkan wang dengan kaedah asal.",
    "apiError.no_cash_session": "Perniagaan belum dibuka (laci tunai). Buka perniagaan dahulu, kemudian terima tunai.",
    "apiError.no_online_methods": "Akaun Stripe anda tiada kaedah bayaran untuk mata wang ini. Semak dalam Stripe.",
    "apiError.not_for_sale": "Ini produk kegunaan salun dan tidak dijual di kaunter.",
    "apiError.not_found": "Tidak dijumpai — mungkin telah dibuang. Muat semula dan cuba lagi.",
    "apiError.not_paid": "Jualan ini belum dibayar.",
    "apiError.not_redeemable": "Hanya perkhidmatan atau produk boleh dibayar dengan pakej.",
    "apiError.not_refundable": "Ini tidak boleh dipulangkan mengikut terma.",
    "apiError.note_required": "Tambah nota yang menerangkan perbezaan sebelum menutup.",
    "apiError.nothing_due": "Tiada lagi baki untuk dibayar bagi jualan ini.",
    "apiError.offline_payment": "Anda di luar talian. Bayaran tidak dibuat giliran — sambung semula dan cuba lagi.",
    "apiError.onboarding_required": "Lengkapkan persediaan salun anda dahulu.",
    "apiError.order_closed": "Jualan ini ditutup dan tidak boleh diubah. Gunakan bayaran balik.",
    "apiError.order_held": "Jualan ini ditangguhkan. Sambung semula dahulu.",
    "apiError.order_locked": "Bayaran telah diterima; item dan harga dikunci. Gunakan bayaran balik.",
    "apiError.package_branch": "Pakej ini tidak boleh digunakan atau dijual di cawangan ini.",
    "apiError.package_closed": "Pakej ini telah ditutup.",
    "apiError.package_exhausted": "Tiada sesi atau nilai berbaki pada pakej ini.",
    "apiError.package_expired": "Pakej ini telah tamat tempoh.",
    "apiError.package_frozen": "Pakej ini dibekukan. Nyahbeku dahulu.",
    "apiError.package_insufficient": "Nilai pakej ini tidak mencukupi.",
    "apiError.package_line_no_options": "Baris pakej tiada pilihan.",
    "apiError.package_not_eligible": "Item ini tidak dilindungi oleh pakej.",
    "apiError.package_other_customer": "Pakej ini milik pelanggan lain.",
    "apiError.package_over_line": "Jumlah pakej melebihi jumlah baris.",
    "apiError.package_unavailable": "Pakej ini tidak boleh digunakan lagi.",
    "apiError.payment_closed": "Bayaran ini telah selesai.",
    "apiError.price_required": "Tetapkan harga setiap item sebelum mengenakan bayaran.",
    "apiError.qty_invalid": "Kuantiti mesti nombor bulat bukan sifar.",
    "apiError.rate_limited": "Terlalu banyak percubaan. Sila tunggu beberapa minit dan cuba lagi.",
    "apiError.redeemed_no_discount": "Baris ini dibayar dengan sesi pakej dan tidak boleh didiskaun. Buang pakej dahulu.",
    "apiError.reference_required": "Masukkan kod kelulusan terminal atau rujukan bank.",
    "apiError.refund_amount_required": "Masukkan jumlah bayaran balik.",
    "apiError.refund_line_duplicate": "Setiap baris hanya sekali bagi setiap bayaran balik.",
    "apiError.refund_method_required": "Pilih cara wang dipulangkan.",
    "apiError.refund_not_package_paid": "Baris ini tidak dibayar dengan pakej.",
    "apiError.refund_over_line": "Bayaran balik melebihi baki baris ini.",
    "apiError.refund_over_paid": "Bayaran balik melebihi jumlah yang dibayar.",
    "apiError.refund_over_quote": "Bayaran balik melebihi jumlah pro-rata.",
    "apiError.restock_done": "Unit tersebut telah dipulangkan ke stok.",
    "apiError.restock_invalid": "Hanya produk yang dijual boleh dipulangkan ke stok (paling banyak kuantiti dijual).",
    "apiError.self_approval": "Permintaan tidak boleh diluluskan oleh orang yang membuatnya.",
    "apiError.service_unavailable": "Perkhidmatan ini tiada di cawangan ini.",
    "apiError.session_closed": "Sesi laci tunai ini telah ditutup.",
    "apiError.session_open": "Laci tunai telah dibuka di cawangan ini.",
    "apiError.split_duplicate": "Orang yang sama muncul dua kali dengan peranan yang sama.",
    "apiError.split_refund": "Bayaran balik kad melebihi mana-mana satu bayaran dalam talian. Pulangkan baki dengan kaedah lain.",
    "apiError.stripe_account_changed": "Akaun Stripe salun telah bertukar. Mulakan bayaran baharu atau buat bayaran balik dalam papan pemuka Stripe.",
    "apiError.stripe_disabled": "Bayaran kad dalam talian tidak diaktifkan. Terima tunai, terminal kad atau pindahan bank.",
    "apiError.stripe_keys_disabled": "Menampal kunci Stripe dinyahdayakan. Gunakan Connect.",
    "apiError.stripe_not_available": "Bayaran kad dalam talian belum tersedia.",
    "apiError.stripe_not_connected": "Sambungkan akaun Stripe salun dahulu (portal pemilik).",
    "apiError.stripe_payment_pending": "Bayaran dalam talian masih terbuka untuk jualan ini. Tunggu atau batalkannya dahulu.",
    "apiError.stripe_verification_pending": "Stripe masih mengesahkan akaun. Bayaran dalam talian bermula sebaik Stripe mengaktifkan caj.",
    "apiError.tendered_short": "Tunai diterima mesti sekurang-kurangnya jumlah tersebut.",
    "apiError.topup_amount_required": "Masukkan jumlah tambah nilai.",
    "apiError.topup_below_min": "Ini di bawah tambah nilai minimum (Tetapan → Kredit & ganjaran).",
    "apiError.topup_no_discount": "Tambah nilai kredit tidak boleh didiskaun. Gunakan peringkat bonus dalam Tetapan.",
    "apiError.topup_single_amount": "Tambah nilai kredit hanya satu jumlah: tiada diskaun, pilihan atau kuantiti.",
    "apiError.total_below_paid": "Jumlah tidak boleh lebih rendah daripada yang telah dibayar.",
    "apiError.unauthorized": "Sila log masuk semula.",
    "apiError.level_in_use": "Tahap ini masih digunakan (ahli pasukan, item harga atau peraturan komisen). Guna “Gabung ke…” untuk memindahkannya, atau tukar dahulu.",
    "apiError.role_in_use": "Peranan ini masih digunakan (peraturan komisen atau lalai jenis baris). Guna “Gabung ke…” untuk memindahkannya, atau tukar dahulu.",
    "apiError.unknown_level": "Tahap staf tidak dikenali. Tahap ditetapkan di Pasukan → Tahap & peranan.",
    "apiError.merge_same": "Pilih entri lain untuk digabungkan.",
    "apiError.industry_required": "Pilih jenis perniagaan anda dahulu.",
    "apiError.unknown_modifier": "Pilihan harga itu tiada untuk perkhidmatan ini.",
    "apiError.unknown_role": "Peranan prestasi tidak diketahui. Tetapkan di Pasukan → Tahap & peranan.",
    "apiError.unknown_variant": "Pilihan itu tiada untuk item ini.",
    "apiError.usage_service_only": "Penggunaan hanya boleh direkod untuk baris perkhidmatan.",
    "apiError.use_credit_refund": "Pulangkan kredit dari tab Kredit pelanggan.",
    "apiError.use_package_refund": "Pulangkan pakej dari tab Pakej pelanggan.",
    "apiError.payment_method_invalid": "Kaedah bayaran itu tidak boleh digunakan di sini. Pilih yang lain.",
    "apiError.refund_over_tip": "Jumlah itu melebihi baki tip yang boleh dipulangkan.",
    "apiError.feature_unavailable": "Pilihan ini tiada untuk kedai ini.",
    "apiError.requested_needs_staff": "Pilih kakitangan yang diminta oleh pelanggan.",
    "apiError.tips_off": "Tip dimatikan untuk kedai ini.",
    "apiError.tip_custom_off": "Jumlah tip tersuai dimatikan. Pilih salah satu tip yang dicadangkan.",
    "apiError.credit_not_for_tip": "Kredit kedai tidak boleh membayar tip. Terima tip secara tunai atau kad.",
    "error.status.400": "Permintaan tidak diterima. Semak butiran dan cuba lagi.",
    "error.status.404": "Tidak dijumpai — mungkin telah dibuang. Muat semula dan cuba lagi.",
    "error.status.409": "Ini bercanggah dengan perubahan terkini. Muat semula dan cuba lagi.",
  },
});
